<template>
  <div
    v-loading="isDataLoading"
    class="estimated-payment"
  >
    <div class="head" />
    <div class="info">
      <div class="wrapper">
        <div class="card">
          <div class="title">
            <span>{{ $t(`affiliates.estimatedPayment.title`) }}</span>
          </div>
          <div class="card-content">
            <div class="field">
              <span class="label">{{ $t(`affiliates.estimatedPayment.total`) }}</span>
              <div class="value color">
                {{ currencyLabel }}{{ $filters.formatMoney(info.amount) }}
              </div>
            </div>
            <div class="field">
              <span class="label">{{ $t(`affiliates.estimatedPayment.next_payment`) }}</span>
              <div class="values">
                {{ $filters.formatDate3(info.next_payment_timestamp) }}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div class="stats">
      <div class="wrapper">
        <div class="content">
          <div class="header">
            <span class="title">{{ $t(`affiliates.estimatedPayment.offer_static`) }}</span>
            <div class="tools">
              <UiDatePicker
                v-if="include_players !== 'to_be_paid_in_next_payment' && include_players !== 'to_be_paid_after_next_payment'"
                v-model="dateRange"
                :placeholder="$t('reports.other.start_end_date')"
                class="t:mr-16"
                :preset-dates-factory="getQuickDates"
                auto-apply
                custom-input
              />
              <UiSelectElement
                v-model="include_players"
                class="estimated-payment__include-select"
                :placeholder="$t(`affiliates.estimatedPayment.players`)"
                :list="playersFilterSelect"
                width="200"
                @change="handleSelectIncludePlayers"
              />
            </div>
          </div>
          <div class="old-table">
            <ElTable
              v-if="!$_.isEmpty(info)"
              :data="info.offers_stats.data"
              :summary-method="getSummaryRow"
              :empty-text="$t('ui.table.no_data')"
              lazy-loading
              show-summary
              border
              class="table"
            >
              <ElTableColumn
                :width="450"
                :label="$t(`affiliates.estimatedPayment.offer`)"
                prop="offer_name"
                align="left"
                header-align="left"
              />
              <ElTableColumn
                :width="96"
                :label="$t('payments.info.table_players')"
                prop="uniq_players_count"
                align="right"
                header-align="right"
              >
                <template v-slot="scope">
                  <div
                    class="row-value"
                  >
                    <span>{{ scope.row.uniq_players_count }}</span>
                  </div>
                </template>
              </ElTableColumn>
              <ElTableColumn
                :width="104"
                :label="getCurrencyHeaderLabel($t(`affiliates.estimatedPayment.revshare`))"
                prop="revshare_revenue"
                align="right"
                header-align="right"
              >
                <template v-slot="scope">
                  <div
                    :class="getCurrencyClass(scope.row.revshare_revenue)"
                    class="row-value"
                  >
                    <span>{{ currencyLabel }}{{ $filters.formatMoney(scope.row.revshare_revenue) }}</span>
                  </div>
                </template>
              </ElTableColumn>
              <ElTableColumn
                :width="96"
                :label="getCurrencyHeaderLabel($t(`affiliates.estimatedPayment.cpa`))"
                prop="cpa_revenue"
                align="right"
                header-align="right"
              >
                <template v-slot="scope">
                  <div
                    :class="getCurrencyClass(scope.row.cpa_revenue)"
                    class="row-value"
                  >
                    <span>{{ currencyLabel }}{{ $filters.formatMoney(scope.row.cpa_revenue) }}</span>
                  </div>
                </template>
              </ElTableColumn>
              <ElTableColumn
                :width="96"
                :label="getCurrencyHeaderLabel($t(`affiliates.estimatedPayment.total`))"
                prop="revenue"
                align="right"
                header-align="right"
              >
                <template v-slot="scope">
                  <div
                    :class="getCurrencyClass(scope.row.revenue)"
                    class="row-value"
                  >
                    <span>{{ currencyLabel }}{{ $filters.formatMoney(scope.row.revenue) }}</span>
                  </div>
                </template>
              </ElTableColumn>
            </ElTable>
          </div>
        </div>
      </div>
    </div>
    <div
      v-loading="isPlayersLoading"
      class="players"
    >
      <div class="wrapper">
        <div class="header">
          <span class="title">{{ $t(`affiliates.estimatedPayment.cpa_player`) }}</span>
          <div class="tools t:flex t:items-center">
            <UiCheckbox
              v-model="hide_stats_after_conversion"
              :label="$t(`affiliates.estimatedPayment.hide_stats`)"
              class="t:mr-8"
              @change="getPlayers"
            />
            <UiSelectTags
              v-model="affiliate_offer_id"
              :placeholder="$t(`affiliates.estimatedPayment.all_offer`)"
              :list="offersSelectOptions"
              width="188"
              @change="handleSelectOffer"
            />
            <UiPagination
              :page="page"
              :page-size="limit"
              :count="count || 1"
              show-size-select
              @page-change="pageChange"
            />
            <div class="select">
              <UiButton
                is-icon
                icon="eye"
                @click="openColumnsSelect"
              />
              <UiSelectElement
                id="select_group"
                ref="select_group"
                v-model="selectedFields"
                class="columns"
                multiple
                @change="handleSelectColumns"
              >
                <template v-slot:options>
                  <ElOption
                    v-for="(item, key) in playersFields"
                    :key="key"
                    :label="`${$t(`affiliates.estimatedPayment.cpaPlayers.fields.${item.name}.title`)}
                  ${$te(`affiliates.estimatedPayment.cpaPlayers.fields.${item.name}.tip`) ? '' : ''}`"
                    :value="item.name"
                  />
                </template>
              </UiSelectElement>
            </div>
            <ExportData
              :data="configExport"
              @export-data="exportPlayers"
            />
          </div>
        </div>
        <UiTable
          :lazy-loading-delay="2000"
          :fields="$_.filter(playersFields, field => selectedFields.includes(field.name))"
          :currency="cpaCurrencyCode"
          :data="playersStats"
          :total="playerSums"
          :rows-count="limit"
          :sort="{
            prop: sortBy,
            order: sortDir,
          }"
          lazy-loading
          show-total
          is-static
          i18n-path="affiliates.estimatedPayment.cpaPlayers"
          class="max-h"
          element-loading-custom-class="data-loader-spinner"
          @sort="handleSortPlayers"
        />
      </div>
    </div>
  </div>
</template>

<script>

import { mapGetters } from 'vuex';
import numeral from 'numeral';
import { endOfDay, format, startOfDay } from 'date-fns';
import ExportData from '@/components/ExportData.vue';
import datePickerShortcuts from '@/views/mixins/date-picker-shortcuts';
import updateUrl from '../../../service/updateUrl';

const LS_KEY = 'EstimatedPayment';

export default {
  name: 'EstimatedPayment',
  components: {
    ExportData,
  },
  mixins: [datePickerShortcuts],
  data() {
    return {
      id: '',
      from: '',
      include_players: 'to_be_paid_in_next_payment',
      affiliate_offer_id: [],
      info: {},
      currencyCode: '',
      cpaCurrencyCode: '',
      affiliateInfo: {},
      isDataLoading: true,
      backLink: {
        link: '',
        title: '',
      },
      exportUrls: {},
      playersStats: [],
      playerSums: {},
      playersFields: [
        {
          name: 'site_player_id',
          minWidth: 150,
          align: 'left',
          sortable: 'custom',
          link: this.getPlayerLink,
          action: () => {},
          actionLib: 'fa',
          controlType: 'icon',
          controlColorIco: () => 'var(--text-negative)',
          popperClass: 'light-tooltip',
          actionName: (row) => (row.is_blocked ? 'lock' : ''),
          computedClass: (_, row) => (row.is_blocked ? 'right-action-icon' : ''),
          tooltip: 'players.info.blocked_by_security_service',
        },
        {
          name: 'external_id',
          minWidth: 110,
          align: 'left',
          sortable: 'custom',
        },
        {
          name: 'subid1',
          minWidth: 100,
          align: 'left',
          sortable: 'custom',
        },
        {
          name: 'subid2',
          minWidth: 100,
          align: 'left',
          sortable: 'custom',
        },
        {
          name: 'subid3',
          minWidth: 100,
          align: 'left',
          sortable: 'custom',
        },
        {
          name: 'subid4',
          minWidth: 100,
          align: 'left',
          sortable: 'custom',
        },
        {
          name: 'subid5',
          minWidth: 100,
          align: 'left',
          sortable: 'custom',
        },
        {
          name: 'player_country_name',
          minWidth: 100,
          align: 'left',
          sortable: 'custom',
        },
        {
          name: 'registration_timestamp',
          minWidth: 135,
          align: 'left',
          sortable: 'custom',
          format: 'date',
          formatOptions: {
            in: '',
            out: 'dd.MM.yyyy HH:mm',
          },
        },
        {
          name: 'site_registration_type_name',
          minWidth: 135,
          align: 'left',
          sortable: 'custom',
        },
        {
          name: 'first_deposit_timestamp',
          minWidth: 140,
          align: 'left',
          sortable: 'custom',
          format: 'date',
          formatOptions: {
            in: '',
            out: 'dd.MM.yyyy HH:mm',
          },
        },
        {
          name: 'conversion_timestamp',
          minWidth: 135,
          align: 'left',
          sortable: 'custom',
          format: 'date',
          formatOptions: {
            in: '',
            out: 'dd.MM.yyyy HH:mm',
          },
        },
        {
          name: 'hold_expiration_timestamp',
          minWidth: 135,
          align: 'left',
          sortable: 'custom',
          format: 'date',
          formatOptions: {
            in: '',
            out: 'dd.MM.yyyy HH:mm',
          },
        },
        {
          name: 'latest_activity_timestamp',
          minWidth: 130,
          align: 'left',
          sortable: 'custom',
          format: 'date',
          formatOptions: {
            in: '',
            out: 'dd.MM.yyyy HH:mm',
          },
        },
        {
          name: 'latest_deposit_timestamp',
          minWidth: 130,
          align: 'left',
          sortable: 'custom',
          format: 'date',
          formatOptions: {
            in: '',
            out: 'dd.MM.yyyy HH:mm',
          },
        },
        {
          name: 'days_since_reg',
          minWidth: 130,
          align: 'right',
          sortable: 'custom',
        },
        {
          name: 'days_active',
          minWidth: 110,
          align: 'right',
          sortable: 'custom',
        },
        {
          name: 'top_deposits_payment_system_name',
          minWidth: 170,
          align: 'left',
          sortable: 'custom',
        },
        {
          name: 'top_withdrawals_payment_system_name',
          minWidth: 170,
          align: 'left',
          sortable: 'custom',
        },
        {
          name: 'deposits_count',
          minWidth: 130,
          align: 'right',
          sortable: 'custom',
        },
        {
          name: 'deposits_sum',
          minWidth: 120,
          align: 'right',
          sortable: 'custom',
          format: 'formatMoney',
        },
        {
          name: 'withdrawals_count',
          minWidth: 150,
          align: 'right',
          sortable: 'custom',
        },
        {
          name: 'withdrawals_sum',
          minWidth: 150,
          align: 'right',
          sortable: 'custom',
          format: 'formatMoney',
        },
        {
          name: 'sport_bets_count',
          minWidth: 140,
          align: 'right',
          sortable: 'custom',
        },
        {
          name: 'sport_bets_sum',
          minWidth: 130,
          align: 'right',
          sortable: 'custom',
          format: 'formatMoney',
        },
        {
          name: 'avg_sport_bets_odd',
          minWidth: 150,
          align: 'right',
          sortable: 'custom',
          format: 'odd',
        },
        {
          name: 'median_sport_bets_odd',
          minWidth: 150,
          align: 'right',
          sortable: 'custom',
          format: 'odd',
        },
        {
          name: 'games_bets_count',
          minWidth: 150,
          align: 'right',
          sortable: 'custom',
        },
        {
          name: 'games_bets_sum',
          minWidth: 140,
          align: 'right',
          sortable: 'custom',
          format: 'formatMoney',
        },
        {
          name: 'betting_profit',
          minWidth: 120,
          align: 'right',
          sortable: 'custom',
          format: 'formatMoney',
        },
        {
          name: 'cpa_revenue',
          minWidth: 120,
          align: 'right',
          sortable: 'custom',
          format: 'formatMoney',
        },
        {
          name: 'revshare_revenue',
          minWidth: 120,
          align: 'right',
          sortable: 'custom',
          format: 'formatMoney',
        },
        {
          name: 'revenue',
          minWidth: 100,
          align: 'right',
          sortable: 'custom',
          format: 'formatMoney',
        },
        {
          name: 'total_profit',
          minWidth: 90,
          align: 'right',
          sortable: 'custom',
          format: 'formatMoney',
          computedClass(value) {
            return value < 0 ? 'negative' : '';
          },
        },
      ],
      selectedFields: [],
      isPlayersLoading: false,
      page: 1,
      limit: 10,
      sortBy: 'site_player_id',
      sortDir: 'asc',
      count: 0,
      hide_stats_after_conversion: false,
      dateRange: [],
    };
  },
  computed: {
    ...mapGetters({
      currencies: 'misc/currencies',
    }),
    configExport() {
      return [
        {
          command: 'csv',
          label: 'CSV',
        },
        {
          command: 'xlsx',
          label: 'XLSX',
        },
        {
          command: 'csv_network',
          label: `CSV ( ${this.$t('affiliates.estimatedPayment.for_net')} )`,
        },
        {
          command: 'xlsx_network',
          label: `XLSX ( ${this.$t('affiliates.estimatedPayment.for_net')} )`,
        },
      ];
    },
    playersFilterSelect() {
      return [
        {
          id: 'to_be_paid_in_next_payment',
          name: this.$t('affiliates.estimatedPayment.playersFilter.to_be_paid_in_next_payment.title'),
        },
        {
          id: 'to_be_paid_after_next_payment',
          name: this.$t('affiliates.estimatedPayment.playersFilter.to_be_paid_after_next_payment.title'),
        },
        {
          id: 'all_unpaid_players',
          name: this.$t('affiliates.estimatedPayment.playersFilter.all_unpaid_players.title'),
        },
        {
          id: 'all_paid_players',
          name: this.$t('affiliates.estimatedPayment.playersFilter.all_paid_players.title'),
        },
        {
          id: 'all_players',
          name: this.$t('affiliates.estimatedPayment.playersFilter.all_players.title'),
        },
      ];
    },
    currencyLabel() {
      const currency = this.currencies.find(((el) => el.code === this.currencyCode));

      return currency?.symbol || `${this.currencyCode} `;
    },
    statisticQuery() {
      const params = {
        include_players: this.include_players,
      };
      if (this.include_players !== 'to_be_paid_in_next_payment' && this.include_players !== 'to_be_paid_after_next_payment' && !this.$_.isEmpty(this.dateRange)) {
        params.start_timestamp = this.dateRange?.[0] ? format(startOfDay(this.dateRange[0]), 'yyyy-MM-dd HH:mm:ss') : '';
        params.end_timestamp = this.dateRange?.[1] ? format(endOfDay(this.dateRange[1]), 'yyyy-MM-dd HH:mm:ss') : '';
      }
      return params;
    },
    playersQuery() {
      const params = {
        limit: this.limit,
        offset: (this.page * this.limit) - this.limit,
        sort_column: this.sortBy,
        sort_dir: this.sortDir,
        hide_stats_after_conversion: this.hide_stats_after_conversion,
        include_players: this.include_players,
        affiliate_offer_id: this.affiliate_offer_id,
      };
      if (this.include_players !== 'to_be_paid_in_next_payment' && this.include_players !== 'to_be_paid_after_next_payment' && !this.$_.isEmpty(this.dateRange)) {
        params.start_timestamp = this.dateRange?.[0] ? format(startOfDay(this.dateRange[0]), 'yyyy-MM-dd HH:mm:ss') : '';
        params.end_timestamp = this.dateRange?.[1] ? format(endOfDay(this.dateRange[1]), 'yyyy-MM-dd HH:mm:ss') : '';
      }
      return params;
    },
    offersSelectOptions() {
      return this.info.offers_stats
        ? this.$_.map(this.info.offers_stats.data, (item) => ({ id: item.affiliate_offer_id, name: item.offer_name }))
        : [];
    },
  },
  watch: {
    limit(value) {
      this.$ls.set(`${LS_KEY}/players_limit`, value || 10);
    },
    dateRange(nv) {
      if (nv === null) {
        this.dateRange = [];
      }
      if (!this.isDataLoading) {
        this.getPaymentsReportInfo();
      }
    },
  },

  created() {
    /*
     * Проверяем hash
     * */
    const _defaultColumns = [
      'site_player_id',
      'player_country_name',
      'registration_timestamp',
      'site_registration_type_name',
      'conversion_timestamp',
      'hold_expiration_timestamp',
      'days_active',
      'deposits_count',
      'deposits_sum',
      'withdrawals_sum',
      'betting_profit',
      'cpa_revenue',
      'revshare_revenue',
      'chargebacks_sum',
      'total_profit',
    ];

    const _paramsHash = updateUrl.getParseParams(this.$route.hash);

    if (!this.$_.isEmpty(_paramsHash)) {
      this.sortBy = this.$_.get(_paramsHash, ['sort_column']) || 'site_player_id';
      this.sortDir = this.$_.get(_paramsHash, ['sort_dir']) || 'asc';
      this.include_players = this.$_.get(_paramsHash, ['include_players']) || this.include_players;
      this.hide_stats_after_conversion = this.$_.get(_paramsHash, ['hide_stats_after_conversion']) || this.hide_stats_after_conversion;
      this.affiliate_offer_id = this.$_.get(_paramsHash, ['affiliate_offer_id']) || this.affiliate_offer_id;
      this.selectedFields = Object.values(this.$_.get(_paramsHash, ['columns']) || _defaultColumns);
      this.limit = Number(this.$_.get(_paramsHash, ['limit'])) || this.limit;
      this.page = Number(this.$_.get(_paramsHash, ['page'])) || 1;
      this.dateRange = [this.$_.get(_paramsHash, ['start_timestamp']), this.$_.get(_paramsHash, ['end_timestamp'])];
    } else {
      //this.dateRange = this.getQuickDates()[4].value.range;
      this.selectedFields = JSON.parse(this.$ls.get('affiliateEstimatedPaymentCpaPlayersColumns')) || _defaultColumns;
      this.limit = this.$ls.get(`${LS_KEY}/players_limit`) || this.limit;
    }
  },

  mounted() {
    this.id = this.$route.params.id;
    this.getPaymentsReportInfo();
    this.$eventBus.$on('changeLocaleGlobal', this.changeLocaleGlobal);
  },

  unmounted() {
    this.$eventBus.$off('changeLocaleGlobal', this.changeLocaleGlobal);
  },

  methods: {
    getCurrencyHeaderLabel(label) {
      if (this.currencyCode) {
        return `${label}, ${this.currencyLabel.trim()}`;
      }

      return label;
    },
    changeLocaleGlobal() {
      this.getPlayers('silent');
    },

    getSummaryRow() {
      const sums = [
        this.$t('affiliates.estimatedPayment.total'),
        this.info.offers_stats.totals.uniq_players_count,
        this.formatCurrency(this.info.offers_stats.totals.revshare_revenue),
        this.formatCurrency(this.info.offers_stats.totals.cpa_revenue),
        this.formatCurrency(this.info.offers_stats.totals.revenue),
      ];
      return sums;
    },
    formatCurrency(value) {
      return `${this.currencyLabel}${value < 0 ? '-' : ''}${numeral(Math.abs(value)).format('0,0.00')}`;
    },
    getCurrencyClass(value) {
      return value < 0 ? 'negative' : '';
    },
    getPaymentsReportInfo() {
      this.isDataLoading = true;
      /*
      * Refresh URL
      * */
      this._completedFilterUrl();

      this.$api.getPaymentsReportInfo(this.id, this.statisticQuery)
        .then((response) => {
          const { payload, misc } = response.data;
          this.currencyCode = misc.currency_code;
          this.info = this.$_.cloneDeepWith(payload);
          this.getPlayers();
        })
        .finally(() => {
          this.isDataLoading = false;
        });
    },

    getPlayers(action) {
      if (action !== 'silent') {
        this.isPlayersLoading = true;
      }

      /*
      * Refresh URL
      * */
      this._completedFilterUrl();

      this.$api.getPaymentsReportCpaPlayersInfo(this.id, this.playersQuery).then((response) => {
        const { payload, misc } = response.data;
        this.count = misc.count;
        this.cpaCurrencyCode = misc.currency_code;
        this.exportUrls = misc.export_urls;
        this.playersStats = payload.data;
        this.playerSums = payload.totals;
      })
        .finally(() => {
          if (action !== 'silent') {
            this.isPlayersLoading = false;
          }
        });
    },

    pageChange(page, size) {
      if (this.page !== page || this.limit !== size) {
        this.page = page;
        this.limit = size;
        this.getPlayers();
      }
    },
    openColumnsSelect() {
      this.$refs.select_group.$refs.uiSelectElement.toggleMenu();
    },
    handleSelectColumns() {
      this.$ls.set('affiliateEstimatedPaymentCpaPlayersColumns', JSON.stringify(this.selectedFields));
    },
    handleSelectOffer() {
      this.getPlayers();
    },
    handleSelectIncludePlayers() {
      this.getPaymentsReportInfo();
    },
    handleSortPlayers({ prop, order }) {
      if (this.sortBy !== prop) {
        this.sortBy = prop;
      } else {
        this.sortDir = order;
      }
      this.getPlayers();
    },
    exportPlayers(format) {
      window.open(this.exportUrls[format]);
    },
    getPlayerLink(row) {
      const isUser = !this.$store.getters['auth/adminAcl'].is_superuser && this.$store.getters['auth/currentAcl'].players_view === 'allow';
      const isSuperUser = this.$store.getters['auth/adminAcl'].is_superuser;
      return isSuperUser || isUser ? `/players/${row.player_id}/info` : false;
    },
    /*
    * Создаем filter URL
    * */
    _completedFilterUrl() {
      const _dataFilters = {
        limit: this.limit,
        page: this.page,
        sort_column: this.sortBy,
        sort_dir: this.sortDir,
        hide_stats_after_conversion: this.hide_stats_after_conversion,
        include_players: this.include_players,
        affiliate_offer_id: this.affiliate_offer_id,
        columns: this.selectedFields,
      };
      if (this.include_players !== 'to_be_paid_in_next_payment' && this.include_players !== 'to_be_paid_after_next_payment' && !this.$_.isEmpty(this.dateRange)) {
        _dataFilters.start_timestamp = this.dateRange?.[0] ? format(startOfDay(this.dateRange[0]), 'yyyy-MM-dd HH:mm:ss') : '';
        _dataFilters.end_timestamp = this.dateRange?.[1] ? format(endOfDay(this.dateRange[1]), 'yyyy-MM-dd HH:mm:ss') : '';
      }
      updateUrl.updateGetParams(_dataFilters);
    },
  },
};
</script>
